import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const KnowledgeBase = () => {
  const [loading, setLoading] = useState(true);
  const [knowledgeData, setKnowledgeData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Categorias disponíveis
  const categories = [
    { id: 'all', name: 'Todas as Categorias' },
    { id: 'frontend', name: 'Frontend' },
    { id: 'backend', name: 'Backend' },
    { id: 'database', name: 'Banco de Dados' },
    { id: 'authentication', name: 'Autenticação' },
    { id: 'simulation', name: 'Simulações' },
    { id: 'reports', name: 'Relatórios' },
    { id: 'configuration', name: 'Configurações' }
  ];

  // Tópicos disponíveis
  const topics = [
    { id: 'all', name: 'Todos os Tópicos' },
    { id: 'error', name: 'Erros' },
    { id: 'feature', name: 'Funcionalidades' },
    { id: 'improvement', name: 'Melhorias' },
    { id: 'bugfix', name: 'Correções de Bugs' },
    { id: 'ui-design', name: 'Design de Interface' },
    { id: 'monitoring', name: 'Monitoramento' },
    { id: 'security', name: 'Segurança' }
  ];

  // Dados da base de conhecimento
  const knowledgeBaseData = [
    {
      id: 31,
      title: 'Diagnóstico e Monitoramento de Erros de API',
      category: 'backend',
      topic: 'monitoring',
      description: 'Implementação de um sistema completo para diagnóstico, monitoramento e registro de erros de API, incluindo um painel de controle dedicado para visualização de logs de erros.',
      problem: 'Erros na API não estavam sendo devidamente registrados, monitorados ou diagnosticados, resultando em falhas silenciosas, dificuldade na identificação de problemas e degradação da experiência do usuário.',
      solution: 'Implementamos um sistema abrangente de registro e monitoramento de erros de API que inclui captura detalhada de informações, sistema de alertas, painel de visualização de logs e estratégias de fallback para garantir a continuidade do serviço mesmo em caso de falhas.',
      codeExample: `// Middleware de captura e registro de erros de API
const errorLoggerMiddleware = (err, req, res, next) => {
  // Extrair informações contextuais
  const error = {
    timestamp: new Date(),
    path: req.path,
    method: req.method,
    ip: req.ip,
    error: {
      name: err.name,
      message: err.message,
      stack: process.env.NODE_ENV === 'production' ? undefined : err.stack,
      status: err.status || 500
    },
    request: {
      headers: req.headers,
      query: req.query,
      params: req.params,
      body: req.method !== 'GET' ? req.body : undefined,
      auth: req.user ? { id: req.user.id, role: req.user.role } : null
    }
  };

  // Registrar no banco de dados
  ErrorLog.create({
    type: 'api_error',
    severity: err.status >= 500 ? 'critical' : 'warning',
    message: err.message,
    details: JSON.stringify(error),
    path: req.path,
    method: req.method,
    ip: req.ip,
    user_id: req.user ? req.user.id : null,
    stack: process.env.NODE_ENV === 'production' ? null : err.stack,
    status_code: err.status || 500,
    resolved: false
  })
  .then(log => {
    console.error(\`[API-ERROR-\${log.id}] \${err.message} - Path: \${req.path}\`);
    
    // Enviar alerta se for erro crítico
    if (err.status >= 500) {
      notifyAdmins(\`Erro crítico na API: \${err.message}\`, log.id);
    }
    
    // Adicionar ID do log à resposta para rastreamento
    res.status(err.status || 500).json({
      error: {
        message: err.message,
        logId: log.id
      }
    });
  })
  .catch(logError => {
    console.error('Erro ao registrar erro da API:', logError);
    next(err);
  });
};

// Uso como middleware global
app.use(errorLoggerMiddleware);`,
      readmeLink: '#31032025---diagnostico-e-monitoramento-de-erros-de-api',
      date: '31/03/2025'
    },
    {
      id: 22,
      title: 'Atualização da Marca e Reorganização do Menu',
      category: 'frontend',
      topic: 'ui-design',
      description: 'Substituição do nome "trabler" para "Simullact.io", reorganização do menu e adição de link para Configurações do Sistema.',
      problem: 'A marca antiga "trabler" não refletia adequadamente o propósito do sistema, e o menu não estava organizado de forma lógica, dificultando o acesso direto às Configurações do Sistema no menu principal.',
      solution: 'Atualizamos a marca para "Simullact.io", uniformizamos as fontes de todo o menu e adicionamos um link direto para Configurações do Sistema.',
      codeExample: `// Atualização do nome da marca no componente MainLayout.js
<h1 className="navbar-brand navbar-brand-autodark">
  <Link to="/">
    <span className="navbar-brand-image" style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fff' }}>Simullact.io</span>
  </Link>
</h1>

// Reorganização do menu com acesso direto às Configurações do Sistema
<li className={\`nav-item \${isActive('/system-settings') ? 'active' : ''}\`}>
  <Link to="/system-settings" className="nav-link">
    <span className="nav-link-icon d-md-none d-lg-inline-block">
      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    </span>
    <span className="nav-link-title">Configurações do Sistema</span>
  </Link>
</li>`,
      readmeLink: '#22032025---atualização-da-marca-e-reorganização-do-menu',
      date: '22/03/2025'
    },
    {
      id: 19,
      title: 'Correção de Erro de Template Strings Aninhadas no ReadmeChat.js',
      category: 'frontend',
      topic: 'bugfix',
      description: 'Correção de um erro de sintaxe causado por template strings aninhadas no arquivo ReadmeChat.js.',
      problem: 'O erro ocorria devido ao uso de template strings aninhadas sem o devido escape, causando problemas de parsing no JavaScript.',
      solution: 'As template strings aninhadas foram corrigidas substituindo o uso de template strings (com backticks) por concatenação de strings tradicional usando o operador +.',
      codeExample: `// Código com erro
const viewDockerLogs = (containerName, timestamp) => {
  window.open(\`/admin/docker-logs?container=\${containerName}&timestamp=\${timestamp}\`, '_blank');
};

// Código corrigido
const viewDockerLogs = (containerName, timestamp) => {
  window.open('/admin/docker-logs?container=' + containerName + '&timestamp=' + timestamp, '_blank');
};`,
      readmeLink: '#19032025---correção-de-erro-de-template-strings-aninhadas-no-readmechat.js',
      date: '19/03/2025'
    },
    {
      id: 11,
      title: 'Melhorias no Modal de Detalhes do Período na Página de Relatórios',
      category: 'frontend',
      topic: 'improvement',
      description: 'Aprimoramento do modal de detalhes do período para exibir informações de forma mais clara e completa.',
      problem: 'O modal de detalhes do período na página de relatórios apresentava limitações na exibição de informações, com valores repetidos, espaçamento inadequado e botões pouco intuitivos.',
      solution: 'Implementamos diversas melhorias: aumentamos o tamanho do modal, adicionamos um card para somatória de remunerações, corrigimos o cálculo do Total Alavancado, melhoramos a visualização das colunas na tabela e substituímos o botão "+" por um ícone mais intuitivo.',
      codeExample: `// 1. Aumento do tamanho do modal
<div className="modal-dialog modal-dialog-centered modal-xl" role="document">

// 2. Adição de card de remuneração total
<div className="col-md-3">
  <div className="card">
    <div className="card-body p-3 text-center">
      <div className="subheader fw-bold">Remuneração</div>
      <div className="h3 text-primary">{formatCurrencyValue(convertCurrency(periodDailyOperations.reduce((sum, op) => sum + (parseFloat(op.daily_withdrawal) || 0), 0)))}</div>
    </div>
  </div>
</div>

// 3. Correção do cálculo do Total Alavancado
<div className="h3 text-azure">{formatCurrencyValue(convertCurrency(periodDailyOperations.reduce((sum, op) => sum + (parseFloat(op.total_profit) || 0), 0)))}</div>

// 4. Melhoria na visualização das colunas
<th style={{ minWidth: '100px', borderRight: '1px solid rgba(98, 105, 118, 0.16)' }}>
  <div>Data</div>
</th>
<th style={{ minWidth: '100px', borderRight: '1px solid rgba(98, 105, 118, 0.16)' }}>
  <div>Operações</div>
  <div>por Dia</div>
</th>

// 5. Substituição do botão "+" por ícone mais intuitivo
<button 
  className="btn btn-sm btn-success"
  title="Ver operações individuais"
  onClick={() => showDayOperations(op.day_number, op.date)}
  style={{ 
    width: '34px', 
    height: '34px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    backgroundColor: '#2fb344',
    borderColor: '#2aa63f'
  }}
>
  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-list-details" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="#ffffff" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
    <path d="M13 5h8" />
    <path d="M13 9h5" />
    <path d="M13 15h8" />
    <path d="M13 19h5" />
    <rect x="3" y="4" width="6" height="6" rx="1" />
    <rect x="3" y="14" width="6" height="6" rx="1" />
  </svg>
</button>`,
      readmeLink: '#16032025---melhorias-no-modal-de-detalhes-do-período-na-página-de-relatórios',
      date: '16/03/2025'
    },
    {
      id: 10,
      title: 'Sistema de Monitoramento de Logs',
      category: 'backend',
      topic: 'feature',
      description: 'Implementação de um sistema completo para visualização e análise de logs de erros e logs do Docker.',
      problem: 'Não havia uma forma centralizada de visualizar e analisar logs de erros do sistema e logs do Docker, dificultando o diagnóstico e resolução de problemas como erros de envio de email.',
      solution: 'Criamos duas páginas dedicadas: uma para logs de erros do sistema e outra para logs do Docker, com funcionalidades de filtragem, exportação e visualização detalhada.',
      codeExample: `// Página de Logs de Erros
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';
import axios from 'axios';

const ErrorLogs = () => {
  const [logs, setLogs] = useState({ errors: [], success: [] });
  const [logsLoading, setLogsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('errors');
  const [filter, setFilter] = useState('');
  const [selectedError, setSelectedError] = useState(null);

  // Função para visualizar logs do Docker
  const viewDockerLogs = (containerName, timestamp) => {
    window.open(\`/admin/docker-logs?container=\${containerName}&timestamp=\${timestamp}\`, '_blank');
  };

  // ... restante do código ...
}

// Página de Logs do Docker
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';

const DockerLogs = () => {
  const [searchParams] = useSearchParams();
  const container = searchParams.get('container');
  const timestamp = searchParams.get('timestamp');
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [timeRange, setTimeRange] = useState('1h');
  const [logLevel, setLogLevel] = useState('all');
  
  // ... restante do código ...
}`,
      readmeLink: '#16032025---implementação-do-sistema-de-monitoramento-de-logs',
      date: '16/03/2025'
    },
    {
      id: 9,
      title: 'Correção do problema de sobreposição do menu de administração',
      category: 'frontend',
      topic: 'bugfix',
      description: 'O menu dropdown de administração estava sendo sobreposto pelo conteúdo principal da aplicação, tornando impossível acessar as opções do menu.',
      problem: 'O menu dropdown de administração na barra lateral estava sendo sobreposto pelo conteúdo principal, impedindo o acesso às opções como "Configuração de Email" e "Dashboard do Sequelize".',
      solution: 'Movemos o menu de administração da barra lateral para o canto superior direito do cabeçalho, junto com o perfil do usuário, e ajustamos o CSS para garantir o posicionamento correto.',
      codeExample: `// Ajuste no HTML/JSX
<div className="navbar-nav flex-row order-md-last ms-auto">
  {isAdmin && (
    <div className="nav-item dropdown me-3">
      <a href="#" className="nav-link dropdown-toggle" onClick={handleDropdownToggle}>
        <span className="nav-link-icon">
          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z" />
          </svg>
        </span>
        <span className="d-none d-md-inline">Administração</span>
      </a>
      <div className="dropdown-menu dropdown-menu-end">
        <Link to="/admin/email-config" className="dropdown-item">
          Configuração de Email
        </Link>
        <Link to="/admin/sequelize" className="dropdown-item">
          Dashboard do Sequelize
        </Link>
      </div>
    </div>
  )}
  
  // Ajustes no CSS
  /* Dropdown no cabeçalho */
  .navbar-nav .dropdown-menu {
    position: absolute;
    right: 0;
    left: auto;
    top: 100%;
    margin-top: 0.5rem;
    z-index: 1500;
  }

  .dropdown-menu-end {
    right: 0;
    left: auto;
  }

  /* Ajustar o alinhamento do navbar para direita */
  .ms-auto {
    margin-left: auto !important;
  }`,
      readmeLink: '#15032025---correção-do-problema-de-sobreposição-do-menu-de-administração',
      date: '15/03/2025'
    },
    {
      id: 1,
      title: 'Erro "prevLogs[type] is not iterable"',
      category: 'frontend',
      topic: 'error',
      description: 'Erro ao acessar a página de resultados da simulação gerada e clicar no botão "Ver Detalhes".',
      problem: 'O erro ocorria na função `addToLog` do componente `Reports.js`, onde o código tentava usar o operador spread (`...`) em `prevLogs[type]`, mas essa propriedade não era um array em algumas situações.',
      solution: 'Modificamos a função `addToLog` para garantir que `prevLogs[type]` seja sempre tratado como um array.',
      codeExample: `const addToLog = (type, message) => {
  const logEntry = {
    timestamp: new Date(),
    message,
    details: typeof message === 'object' ? JSON.stringify(message) : message
  };
  
  setLogs(prevLogs => {
    // Garantir que prevLogs seja um objeto válido
    const safeObj = prevLogs || {};
    
    // Garantir que prevLogs[type] seja um array
    const existingLogs = Array.isArray(safeObj[type]) ? safeObj[type] : [];
    
    return {
      ...safeObj,
      [type]: [logEntry, ...existingLogs].slice(0, 100)
    };
  });
};`,
      readmeLink: '#14032025---correção-do-erro-prevlogstype-is-not-iterable',
      date: '14/03/2025'
    },
    {
      id: 7,
      title: 'Redefinição de Senha do Administrador',
      category: 'authentication',
      topic: 'security',
      description: 'Procedimento para redefinir a senha do administrador quando necessário.',
      problem: 'Administrador esqueceu a senha e não consegue acessar o sistema.',
      solution: 'Criamos um script SQL para atualizar a senha do administrador diretamente no banco de dados.',
      codeExample: `// Script para atualizar a senha do administrador
const bcrypt = require('bcryptjs');
const { User } = require('./backend/models');

async function updateAdminPassword() {
  try {
    const adminUser = await User.findOne({ where: { email: 'admin@example.com' } });
    
    if (!adminUser) {
      console.error('Usuário administrador não encontrado!');
      return;
    }
    
    const salt = await bcrypt.genSalt(10);
    const hashedPassword = await bcrypt.hash('tigre@123', salt);
    
    await adminUser.update({ password: hashedPassword });
    
    console.log('Senha do administrador atualizada com sucesso!');
    console.log('Email: admin@example.com');
    console.log('Nova senha: tigre@123');
  } catch (error) {
    console.error('Erro ao atualizar senha do administrador:', error);
  }
}`,
      readmeLink: '#15032025---redefinição-de-senha-do-administrador',
      date: '15/03/2025'
    },
    {
      id: 2,
      title: 'Erro ao carregar detalhes de relatórios',
      category: 'frontend',
      topic: 'error',
      description: 'Após corrigir o erro "prevLogs[type] is not iterable", ainda persistia um problema ao tentar visualizar os detalhes das simulações na página de relatórios.',
      problem: 'Ao clicar no botão "Ver Detalhes" para uma simulação, aparecia a mensagem de erro: "Erro ao carregar detalhes. Tente novamente."',
      solution: 'Identificamos que a coluna "total_operation_withdrawals" estava ausente na tabela daily_operations e criamos um arquivo de migração para adicionar a coluna.',
      codeExample: `-- Migração para adicionar a coluna total_operation_withdrawals à tabela daily_operations
ALTER TABLE daily_operations ADD COLUMN IF NOT EXISTS total_operation_withdrawals DECIMAL(15, 2) NOT NULL DEFAULT 0;`,
      readmeLink: '#14032025---correção-do-erro-ao-carregar-detalhes-de-relatórios',
      date: '14/03/2025'
    },
    {
      id: 3,
      title: 'Perda de histórico de simulações',
      category: 'simulation',
      topic: 'bugfix',
      description: 'Problema crítico na funcionalidade de simulações: ao criar novas simulações, o sistema estava sobrescrevendo o histórico de simulações anteriores.',
      problem: 'Todas as simulações estavam compartilhando as mesmas configurações de investimento, o que fazia com que, quando uma nova simulação era criada, todas as simulações anteriores fossem afetadas.',
      solution: 'Modificamos a rota de criação de simulações para criar uma cópia independente das configurações de investimento para cada nova simulação e criamos um script de migração para corrigir simulações existentes.',
      codeExample: `// Criar uma cópia das configurações para preservar o histórico
const settingsCopy = await InvestmentSetting.create({
  user_id: req.userId,
  initial_amount: settings.initial_amount,
  exchange_rate: settings.exchange_rate,
  entry_fee: settings.entry_fee,
  exit_fee: settings.exit_fee,
  profit_rate: settings.profit_rate,
  daily_withdrawal_rate: settings.daily_withdrawal_rate,
  operations_per_day: settings.operations_per_day,
  simulation_months: settings.simulation_months,
  start_date: settings.start_date,
  created_at: new Date(),
  updated_at: new Date()
});`,
      readmeLink: '#15032025---correção-do-problema-de-perda-de-histórico-de-simulações',
      date: '15/03/2025'
    },
    {
      id: 4,
      title: 'Erro de coluna ausente no banco de dados',
      category: 'database',
      topic: 'error',
      description: 'Erro ao tentar acessar a coluna "simulation_months" que não existia na tabela "investment_settings".',
      problem: 'A coluna "simulation_months" foi adicionada ao modelo, mas não estava presente na tabela do banco de dados.',
      solution: 'Criamos um script SQL para adicionar a coluna ausente à tabela "investment_settings".',
      codeExample: `-- Adicionar coluna simulation_months à tabela investment_settings
ALTER TABLE investment_settings ADD COLUMN IF NOT EXISTS simulation_months INTEGER DEFAULT 24;
UPDATE investment_settings SET simulation_months = 24 WHERE simulation_months IS NULL;`,
      readmeLink: '#14032025---correção-do-erro-de-coluna-ausente-no-banco-de-dados',
      date: '14/03/2025'
    },
    {
      id: 5,
      title: 'Erro de coluna ausente na tabela operations',
      category: 'database',
      topic: 'error',
      description: 'Erro ao tentar acessar a coluna "operation_withdrawal" que não existia na tabela "operations".',
      problem: 'A coluna "operation_withdrawal" foi adicionada ao modelo, mas não estava presente na tabela do banco de dados.',
      solution: 'Criamos um script SQL para adicionar a coluna ausente à tabela "operations".',
      codeExample: `-- Adicionar coluna operation_withdrawal à tabela operations
ALTER TABLE operations ADD COLUMN IF NOT EXISTS operation_withdrawal DECIMAL(15, 2) NOT NULL DEFAULT 0;`,
      readmeLink: '#14032025---correção-do-erro-de-coluna-ausente-na-tabela-operations',
      date: '14/03/2025'
    },
    {
      id: 6,
      title: 'Implementação do Dashboard do Sequelize',
      category: 'backend',
      topic: 'feature',
      description: 'Implementação de um dashboard para monitorar os modelos do Sequelize e o banco de dados.',
      problem: 'Não havia uma forma fácil de monitorar os modelos do Sequelize e verificar se o banco de dados estava sincronizado com os modelos.',
      solution: 'Criamos um dashboard administrativo que exibe todos os modelos do Sequelize, verifica se há colunas ausentes no banco de dados e exibe estatísticas sobre os modelos.',
      codeExample: `// Rota para listar todos os modelos do Sequelize
router.get('/sequelize/models', auth, adminAuth, async (req, res) => {
  try {
    const models = Object.keys(sequelize.models).map(modelName => {
      const model = sequelize.models[modelName];
      return {
        name: modelName,
        attributes: Object.keys(model.rawAttributes).map(attrName => ({
          name: attrName,
          type: model.rawAttributes[attrName].type.toString(),
          allowNull: !!model.rawAttributes[attrName].allowNull,
          defaultValue: model.rawAttributes[attrName].defaultValue
        })),
        associations: Object.keys(model.associations).map(assocName => ({
          name: assocName,
          type: model.associations[assocName].associationType,
          target: model.associations[assocName].target.name
        }))
      };
    });
    
    res.json(models);
  } catch (error) {
    console.error('Erro ao listar modelos do Sequelize:', error);
    res.status(500).json({ message: 'Erro no servidor.' });
  }
});`,
      readmeLink: '#dashboard-do-sequelize',
      date: '14/03/2025'
    },
    {
      id: 8,
      title: 'Correção de erros de importação e Router duplicado',
      category: 'frontend',
      topic: 'bugfix',
      description: 'Correção de erros que impediam a inicialização correta da aplicação frontend.',
      problem: 'A aplicação apresentava dois erros principais: módulos não encontrados e Router duplicado no React Router.',
      solution: 'Corrigimos os caminhos de importação dos componentes de administração e removemos o Router duplicado no App.js.',
      codeExample: `// 1. Correção dos caminhos de importação no App.js
// De:
import EmailConfig from './pages/admin/EmailConfig';
import SequelizeDashboard from './pages/admin/SequelizeDashboard';
import KnowledgeBase from './pages/admin/KnowledgeBase';

// Para:
import EmailConfig from './pages/EmailConfig';
import SequelizeDashboard from './pages/SequelizeDashboard';
import KnowledgeBase from './pages/KnowledgeBase';

// 2. Correção do Router duplicado no App.js
// De:
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
// ...
function App() {
  return (
    <Router>
      <ToastContainer position="top-right" autoClose={5000} />
      <Routes>
        // ...
      </Routes>
    </Router>
  );
}

// Para:
import { Routes, Route, Navigate } from 'react-router-dom';
// ...
function App() {
  return (
    <>
      <ToastContainer position="top-right" autoClose={5000} />
      <Routes>
        // ...
      </Routes>
    </>
  );
}`,
      readmeLink: '#15032025---correção-de-erros-de-importação-e-router-duplicado',
      date: '15/03/2025'
    }
  ];

  useEffect(() => {
    // Verificar se o usuário é administrador
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsAdmin(user.email === 'admin@example.com');
    
    // Carregar dados da base de conhecimento
    setKnowledgeData(knowledgeBaseData);
    setLoading(false);
  }, []);

  // Filtrar dados com base na pesquisa e filtros
  const filteredData = knowledgeData.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.problem.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.solution.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesTopic = selectedTopic === 'all' || item.topic === selectedTopic;
    
    return matchesSearch && matchesCategory && matchesTopic;
  });

  // Função para renderizar o ícone da categoria
  const renderCategoryIcon = (category) => {
    switch (category) {
      case 'frontend':
        return <i className="fas fa-desktop text-primary me-2"></i>;
      case 'backend':
        return <i className="fas fa-server text-success me-2"></i>;
      case 'database':
        return <i className="fas fa-database text-info me-2"></i>;
      case 'authentication':
        return <i className="fas fa-lock text-warning me-2"></i>;
      case 'simulation':
        return <i className="fas fa-chart-line text-danger me-2"></i>;
      case 'reports':
        return <i className="fas fa-file-alt text-secondary me-2"></i>;
      case 'configuration':
        return <i className="fas fa-cog text-dark me-2"></i>;
      default:
        return <i className="fas fa-question-circle text-muted me-2"></i>;
    }
  };

  // Função para renderizar o badge do tópico
  const renderTopicBadge = (topic) => {
    switch (topic) {
      case 'error':
        return <span className="badge bg-danger">Erro</span>;
      case 'feature':
        return <span className="badge bg-success">Funcionalidade</span>;
      case 'improvement':
        return <span className="badge bg-info">Melhoria</span>;
      case 'bugfix':
        return <span className="badge bg-warning">Correção de Bug</span>;
      default:
        return <span className="badge bg-secondary">Outro</span>;
    }
  };

  return (
    <div className="container-xl p-0">
      {/* Header estilo Developer Documentation */}
      <div className="d-flex align-items-center justify-content-between py-3 border-bottom" style={{ background: '#f8f9fa' }}>
        <div className="d-flex align-items-center">
          <h2 className="page-title mb-0 ms-3">Base de Conhecimento</h2>
        </div>
        <div className="d-flex align-items-center me-3">
          {isAdmin && (
            <Link to="/admin" className="btn btn-outline-secondary me-2">
              <i className="fas fa-arrow-left me-2"></i>
              Voltar
            </Link>
          )}
          <Link to="/admin/knowledge-dashboard" className="btn btn-outline-primary me-2">
            <i className="fas fa-columns me-2"></i>
            Dashboard Kanban
          </Link>
          <button 
            className="btn btn-link text-decoration-none p-2"
            onClick={() => setIsFavorite(!isFavorite)}
          >
            <i className={`fas fa-star fs-4 ${isFavorite ? 'text-warning' : 'text-muted'}`}></i>
          </button>
          <button className="btn btn-primary ms-2">
            <i className="fas fa-plus me-2"></i>
            Criar
          </button>
          <div className="dropdown ms-2">
            <button className="btn btn-light" type="button" data-bs-toggle="dropdown">
              <i className="fas fa-ellipsis-v"></i>
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li><a className="dropdown-item" href="#"><i className="fas fa-edit me-2"></i>Editar</a></li>
              <li><a className="dropdown-item" href="#"><i className="fas fa-share me-2"></i>Compartilhar</a></li>
              <li><hr className="dropdown-divider" /></li>
              <li><a className="dropdown-item text-danger" href="#"><i className="fas fa-trash me-2"></i>Excluir</a></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Barra de navegação com abas */}
      <div className="border-bottom">
        <ul className="nav nav-tabs border-0">
          <li className="nav-item">
            <a 
              className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`} 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('overview');
              }}
            >
              <i className="fas fa-home me-2"></i>
              Visão Geral
            </a>
          </li>
          <li className="nav-item">
            <a 
              className={`nav-link ${activeTab === 'features' ? 'active' : ''}`} 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('features');
              }}
            >
              <i className="fas fa-puzzle-piece me-2"></i>
              Funcionalidades
            </a>
          </li>
          <li className="nav-item">
            <a 
              className={`nav-link ${activeTab === 'solutions' ? 'active' : ''}`} 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('solutions');
              }}
            >
              <i className="fas fa-lightbulb me-2"></i>
              Soluções
            </a>
          </li>
          <li className="nav-item">
            <a 
              className={`nav-link ${activeTab === 'faq' ? 'active' : ''}`} 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab('faq');
              }}
            >
              <i className="fas fa-question-circle me-2"></i>
              FAQ
            </a>
          </li>
          <li className="nav-item ms-auto">
            <div className="input-icon mt-1 me-3">
              <input 
                type="text" 
                className="form-control form-control-sm" 
                placeholder="Pesquisar na documentação..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="input-icon-addon">
                <i className="fas fa-search"></i>
              </span>
            </div>
          </li>
        </ul>
      </div>

      <div className="page-body p-3">
        <div className="row">
          <div className="col-12 col-md-3">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Categorias</h3>
              </div>
              <div className="list-group list-group-flush">
                {categories.map(category => (
                  <a 
                    key={category.id}
                    href="#" 
                    className={`list-group-item list-group-item-action ${selectedCategory === category.id ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedCategory(category.id);
                    }}
                  >
                    {category.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header">
                <h3 className="card-title">Tópicos</h3>
              </div>
              <div className="list-group list-group-flush">
                {topics.map(topic => (
                  <a 
                    key={topic.id}
                    href="#" 
                    className={`list-group-item list-group-item-action ${selectedTopic === topic.id ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedTopic(topic.id);
                    }}
                  >
                    {topic.name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="col-12 col-md-9">
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-3">Carregando base de conhecimento...</p>
                  </div>
                ) : filteredData.length === 0 ? (
                  <div className="empty">
                    <div className="empty-img">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-search" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"></path>
                        <path d="M21 21l-6 -6"></path>
                      </svg>
                    </div>
                    <p className="empty-title">Nenhum resultado encontrado</p>
                    <p className="empty-subtitle text-muted">
                      Tente ajustar os filtros ou termos de pesquisa para encontrar o que está procurando.
                    </p>
                    <div className="empty-action">
                      <button 
                        className="btn btn-primary" 
                        onClick={() => {
                          setSearchTerm('');
                          setSelectedCategory('all');
                          setSelectedTopic('all');
                        }}
                      >
                        Limpar filtros
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-vcenter card-table">
                      <thead>
                        <tr>
                          <th width="60">ID</th>
                          <th>Título</th>
                          <th>Categoria</th>
                          <th>Tópico</th>
                          <th>Data</th>
                          <th className="w-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map(item => (
                          <tr key={item.id}>
                            <td className="text-center fw-bold">{item.id}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                {renderCategoryIcon(item.category)}
                                <div>
                                  <a href="#" className="text-reset" data-bs-toggle="modal" data-bs-target={`#modal-${item.id}`}>
                                    {item.title}
                                  </a>
                                  <div className="text-muted">{item.description.substring(0, 60)}...</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              {categories.find(cat => cat.id === item.category)?.name || item.category}
                            </td>
                            <td>
                              {renderTopicBadge(item.topic)}
                            </td>
                            <td className="text-muted">
                              {item.date}
                            </td>
                            <td>
                              <a href="#" className="btn btn-primary btn-sm" data-bs-toggle="modal" data-bs-target={`#modal-${item.id}`}>
                                Ver
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer footer-transparent d-print-none mt-auto">
        <div className="container-xl">
          <div className="row text-center align-items-center flex-row-reverse">
            <div className="col-12 col-lg-auto mt-3 mt-lg-0">
              <ul className="list-inline list-inline-dots mb-0">
                <li className="list-inline-item">
                  Copyright © 2023 Plataforma de Investimentos
                </li>
                <li className="list-inline-item">
                  <a href="#" className="link-secondary">Termos de Uso</a>
                </li>
                <li className="list-inline-item">
                  <a href="#" className="link-secondary">Política de Privacidade</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Modais para cada item */}
      {knowledgeData.map(item => (
        <div className="modal modal-blur fade" id={`modal-${item.id}`} tabIndex="-1" key={`modal-${item.id}`}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{item.title}</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <h4>Descrição</h4>
                  <p>{item.description}</p>
                </div>
                
                <div className="mb-3">
                  <h4>Problema</h4>
                  <div className="alert alert-danger">
                    <div className="d-flex">
                      <div>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                      </div>
                      <div>
                        {item.problem}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mb-3">
                  <h4>Solução</h4>
                  <div className="alert alert-success">
                    <div className="d-flex">
                      <div>
                        <i className="fas fa-check-circle me-2"></i>
                      </div>
                      <div>
                        {item.solution}
                      </div>
                    </div>
                  </div>
                </div>
                
                {item.codeExample && (
                  <div className="mb-3">
                    <h4>Exemplo de Código</h4>
                    <div className="card">
                      <div className="card-header">
                        <div className="card-actions">
                          <a href="#" className="btn btn-sm btn-outline-primary" onClick={(e) => {
                            e.preventDefault();
                            navigator.clipboard.writeText(item.codeExample);
                            toast.success('Código copiado para a área de transferência!');
                          }}>
                            <i className="fas fa-copy me-1"></i> Copiar
                          </a>
                        </div>
                      </div>
                      <pre className="language-javascript p-3 bg-dark text-white" style={{ borderRadius: '0 0 4px 4px', margin: 0 }}>
                        <code>{item.codeExample}</code>
                      </pre>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <a 
                  href={`/readme-chat${item.readmeLink}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn btn-outline-primary me-auto"
                >
                  <i className="fas fa-external-link-alt me-2"></i>
                  Ver documentação completa
                </a>
                <button type="button" className="btn btn-primary" data-bs-dismiss="modal">Fechar</button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default KnowledgeBase; 